import React, { memo, useMemo } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Icon, Typography } from "@mui/material";
import { sanitizeText } from "@/helpers/sanitizeHelper";
import { getCreativePackageChannelLabel } from "@/features/campaigns/digital/launch-digital-campaign/helpers/creativeLabel";
import CreativeMediaPreview from "./CreativeMediaPreview";

type CreativeChannel = {
  id?: string | number;
  channel?: {
    channel_name?: string;
  };
  channel_name?: string;
  attachment?: string;
  [key: string]: any;
};

type CreativeCarouselProps = {
  channels?: CreativeChannel[];
  slidesToShow?: number;
};

type ArrowProps = {
  className?: string;
  style?: React.CSSProperties;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
};

const CreativeCarousel = ({
  channels = [],
  slidesToShow = 3,
}: CreativeCarouselProps) => {
  const settings = useMemo(() => {
    const totalSlides = channels.length || 1;
    const getSlidesToShow = (preferredSlides: number) =>
      Math.max(1, Math.min(preferredSlides, totalSlides));

    return {
      infinite: false,
      slidesToShow: getSlidesToShow(slidesToShow),
      speed: 500,
      responsive: [
        {
          breakpoint: 1024,
          settings: {
            slidesToShow: getSlidesToShow(Math.min(slidesToShow, 2)),
            slidesToScroll: 1,
          },
        },
        {
          breakpoint: 991,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
          },
        },
        {
          breakpoint: 766,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
          },
        },
        {
          breakpoint: 480,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
          },
        },
      ],
      nextArrow: <SampleNextArrow />,
      prevArrow: <SamplePrevArrow />,
    };
  }, [channels.length, slidesToShow]);
  return (
    <div className="creative-carousel d-flex align-items-center justify-content-center">
      <Slider {...settings} className="w-100">
        {channels.map((ch) => {
          const channelName = ch.channel?.channel_name || ch.channel_name || "";
          const creativeLabel = getCreativePackageChannelLabel(ch);

          return (
            <div key={ch.id}>
              <div
                className={`creative-channel ${
                  ch.disabled ? "creative-channel-disabled" : ""
                }`}
                aria-disabled={ch.disabled ? "true" : undefined}
                data-testid={
                  ch.disabled ? "disabled-creative-channel" : undefined
                }
              >
                {channelName ? (
                  <Typography variant="caption" component="p" gutterBottom>
                    Channel:{" "}
                    <Typography
                      color="text.primary"
                      variant="caption"
                      className="fw-b"
                    >
                      {channelName}
                    </Typography>
                  </Typography>
                ) : (
                  ""
                )}

                <CreativeMediaPreview
                  attachment={ch.attachment}
                  channelName={sanitizeText(channelName || "Creative")}
                  creativeName={creativeLabel}
                  creativeType={ch.creative_type}
                  thumbnail={ch.thumbnail_image || ch.thumbnail || ch.poster}
                  poster={ch.poster}
                  imageClassName="img-fluid"
                />
                <Typography variant="caption" component="p" className="mt-2">
                  <Typography
                    color="text.primary"
                    variant="caption"
                    className="fw-b"
                  >
                    {creativeLabel}
                  </Typography>
                </Typography>
              </div>
            </div>
          );
        })}
      </Slider>
    </div>
  );
};

function SampleNextArrow(props: ArrowProps) {
  const { className, style, onClick } = props;
  return (
    <div
      className={className}
      style={{ ...style, display: "flex" }}
      onClick={onClick}
    >
      <Icon color={onClick ? "primary" : "disabled"}>navigate_next</Icon>
    </div>
  );
}

function SamplePrevArrow(props: ArrowProps) {
  const { className, style, onClick } = props;
  return (
    <div
      className={className}
      style={{ ...style, display: "flex" }}
      onClick={onClick}
    >
      <Icon color={onClick ? "primary" : "disabled"}>navigate_before</Icon>
    </div>
  );
}

export default memo(CreativeCarousel);
